// src/lib/stats-sousloc.js — Lot Statistiques : le modèle SOUS-LOCATION.
//
// ⚠️ MODULE PUR — ZÉRO I/O. Aucun appel Supabase / réseau / React ici.
//
// LE MODÈLE (formule actée Dimitri 2026-08-28) : la sous-location (Maison Zen,
// chambres lc1/lc2/lc3) ne rapporte PAS une commission — FBZ paie le loyer et
// les charges au propriétaire et garde ce que rapportent les chambres :
//
//   marge = CA des chambres − loyer versé − charges portées
//
// SOURCE DU LOYER : les lignes MENSUELLES saisies dans Facturation
// (data[mois]["l8"].loyer + .chargesVar — SousLocationView). RIEN n'est créé :
// on réutilise calcSousLocForLog / allocSousLocCharges (src/lib/calc.js), les
// MÊMES fonctions que Compta, pour que tous les écrans disent le même chiffre.
//
// CLÉ DE RÉPARTITION entre chambres : PARTS ÉGALES 1/N (actée Dimitri —
// « le loyer ne baisse pas parce qu'une chambre se loue moins bien ; une
// chambre en perte à un tiers est une information que je veux voir ») — c'est
// le comportement d'allocSousLocCharges (invariant testé).
//
// SCOPE : si seules certaines chambres sont cochées dans le filtre, on
// n'additionne que LEURS parts (1/N chacune) — le loyer des chambres non
// cochées n'est pas réparti sur les cochées. `selectionPartielle` le signale
// pour que l'UI puisse l'expliquer (la marge affichée n'est alors pas la marge
// de toute la maison).
//
// Retour : { parMois, total, parChambre, hasActivity }
//   parMois[mk]  = { ca, loyer, charges, marge } (parts des chambres du scope)
//   total        = mêmes clés, Σ sur la période
//   parChambre   = [{ id, nom, ca, loyer, charges, marge }] Σ sur la période
//   hasActivity  = au moins un montant non nul sur la période (CA ou loyer ou
//                  charges) — pilote l'état vide de l'UI.
// Montants round2 À LA FIN (sommes non arrondies, arrondi sur l'agrégat).

import { allocSousLocCharges } from './calc'

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100
}

// Les logs parents sous-loc = les logs du groupe p6 qui portent des chambres
// (même périmètre que calcBenefSousLoc côté Compta).
export function getSousLocParents(cfg) {
  const out = []
  for (const p of cfg?.props || []) {
    if (p.id !== 'p6') continue
    for (const log of p.logs || []) {
      if ((log.chambres?.length || 0) > 0) out.push(log)
    }
  }
  return out
}

// ── buildSousLocStats({ cfg, dataProjete, blocksByMonth, period, scopeLids }) ──
export function buildSousLocStats({
  cfg,
  dataProjete,
  blocksByMonth,
  period,
  scopeLids,
} = {}) {
  const months = Array.isArray(period) ? period : []
  const scopeSet = new Set(Array.isArray(scopeLids) ? scopeLids : [])
  const parents = getSousLocParents(cfg)

  const parMois = {}
  const total = { ca: 0, loyer: 0, charges: 0, marge: 0 }
  // Accumulation par chambre (ordre de cfg conservé, dédup par id via alloc).
  const chambreAcc = new Map() // id → { id, nom, ca, loyer, charges }
  let nbChambresTotal = 0
  let nbChambresScope = 0

  // Le nombre de chambres dans/hors scope ne dépend pas du mois — compté une fois.
  for (const parent of parents) {
    const seen = new Set()
    for (const ch of parent.chambres || []) {
      if (!ch || seen.has(ch.id)) continue
      seen.add(ch.id)
      nbChambresTotal += 1
      if (scopeSet.has(ch.id)) nbChambresScope += 1
    }
  }
  const selectionPartielle = nbChambresScope > 0 && nbChambresScope < nbChambresTotal

  for (const mk of months) {
    const acc = { ca: 0, loyer: 0, charges: 0 }
    for (const parent of parents) {
      // MÊME moteur que Compta : allocSousLocCharges répartit loyer + charges
      // du parent en 1/N parts égales, blocs direct internes inclus dans le CA.
      const alloc = allocSousLocCharges(parent, mk, dataProjete, blocksByMonth)
      const nomByCh = new Map((parent.chambres || []).map((c) => [c?.id, c?.nom]))
      for (const part of alloc) {
        if (!scopeSet.has(part.chambreId)) continue
        acc.ca += part.caChambre
        acc.loyer += part.loyerAlloue
        acc.charges += part.chargesAllouees
        let row = chambreAcc.get(part.chambreId)
        if (!row) {
          row = {
            id: part.chambreId,
            nom: nomByCh.get(part.chambreId) || part.chambreId,
            ca: 0,
            loyer: 0,
            charges: 0,
          }
          chambreAcc.set(part.chambreId, row)
        }
        row.ca += part.caChambre
        row.loyer += part.loyerAlloue
        row.charges += part.chargesAllouees
      }
    }
    parMois[mk] = {
      ca: round2(acc.ca),
      loyer: round2(acc.loyer),
      charges: round2(acc.charges),
      marge: round2(acc.ca - acc.loyer - acc.charges),
    }
    total.ca += acc.ca
    total.loyer += acc.loyer
    total.charges += acc.charges
  }

  const parChambre = [...chambreAcc.values()].map((r) => ({
    id: r.id,
    nom: r.nom,
    ca: round2(r.ca),
    loyer: round2(r.loyer),
    charges: round2(r.charges),
    marge: round2(r.ca - r.loyer - r.charges),
  }))

  const totalOut = {
    ca: round2(total.ca),
    loyer: round2(total.loyer),
    charges: round2(total.charges),
    marge: round2(total.ca - total.loyer - total.charges),
  }

  const hasActivity =
    totalOut.ca !== 0 || totalOut.loyer !== 0 || totalOut.charges !== 0

  return { parMois, total: totalOut, parChambre, selectionPartielle, hasActivity }
}
